import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { Button } from 'antd';
import { useRouter } from 'next/router';
import { RootState } from '@/models';
import SupplyInfoContent from '../SupplyInfoContent';
import UnlockConfirmModalContent from '../UnlockConfirmModalContent';
import UnlockNumErrorModalContent from '../UnlockNumErrorModalContent';
import styles from './index.module.scss';

export type LockCardState = 'not_member' | 'empty' | 'locked' | 'unlocked';
export type LockedCardProps = {
  /** 品牌id */
  conBrandId?: string;
  /** 迷你模式 */
  mini?: boolean;
  /** 背景图链接 */
  backgroundImgUrl?: string;
  /** 前景图链接 */
  foregroundImgUrl?: string;
  /** 说明文字 */
  explanation?: React.ReactNode;
  /** 按钮文字 */
  btnContent?: React.ReactNode;
  /** 卡片状态 */
  state?: LockCardState;
  /** 补充信息默认打开的tab标签key */
  tabKey?: 'coreTeam' | 'supplyChain';
  className?: string;
  explanationClassName?: string;
  /** 卡片状态改变时触发的回调 */
  onStateChange?: (state: string) => void;
};

const LockedCard: React.FC<LockedCardProps> = ({
  conBrandId,
  mini = false,
  backgroundImgUrl = '//assets-oss.cbndata.com/cbndata-refactor-fe/FnWReSbLyFLen9WYur2j_sIsJ13A.png',
  foregroundImgUrl = '//assets-oss.cbndata.com/cbndata-refactor-fe/FmMLM1Kied4_R9L7XxLAxzTLx_vN.png',
  explanation,
  btnContent,
  state = 'not_member',
  tabKey = '',
  className,
  explanationClassName,
  onStateChange,
}) => {
  const { token } = useSelector((state: RootState) => state.user);
  const { memberType, memberTypeName, alreadyUnLockNum, totalCanUnLockNum, canUnLockBalanceNum } = useSelector(
    (state: RootState) => state.user.memberInfo
  );

  const onUnlocked = useCallback(() => {
    onStateChange?.('unlocked');
  }, [onStateChange]);
  const STATE_MAP = useMemo(
    () => ({
      // 提示升级会员
      not_member: {
        btnText: '了解详情',
        explanationText: '',
      },
      // 提示补充信息
      empty: {
        btnText: '补充信息',
        explanationText: '',
      },
      locked: {
        btnText: `解锁(${alreadyUnLockNum}/${totalCanUnLockNum})`,
        explanationText: `您已开通中国新消费品牌数据库会员（${memberTypeName}）是否解锁查看完整数据`,
      },
      unlocked: {
        btnText: '',
        explanationText: '',
      },
    }),
    [alreadyUnLockNum, memberTypeName, totalCanUnLockNum]
  );
  const btnText = useMemo(
    () => btnContent || (!token ? '登录' : STATE_MAP[state]?.btnText || ''),
    [btnContent, token, STATE_MAP, state]
  );
  const explanationText = useMemo(
    () => (!token ? '若要查看更多数据，请先登录' : memberType ? STATE_MAP[state]?.explanationText : explanation),
    [STATE_MAP, explanation, memberType, state, token]
  );
  const dispatch = useDispatch();
  const router = useRouter();
  const onClick = useCallback(() => {
    if (!token) {
      dispatch({ type: 'modal/openUserLoginModal', payload: 'login' });
    } else if (state === 'empty') {
      dispatch({
        type: 'modal/open',
        payload: {
          content: <SupplyInfoContent conBrandId={conBrandId} defaultActiveKey={tabKey} />,
          title: '补充信息',
        },
      });
    } else if (state === 'locked') {
      canUnLockBalanceNum
        ? dispatch({
            type: 'modal/open',
            payload: {
              content: <UnlockConfirmModalContent conBrandId={conBrandId} onUnlocked={onUnlocked} />,
            },
          })
        : dispatch({
            type: 'modal/open',
            payload: {
              content: <UnlockNumErrorModalContent />,
            },
          });
    } else {
      router.push('/member');
    }
  }, [canUnLockBalanceNum, conBrandId, dispatch, onUnlocked, router, state, tabKey, token]);
  return (
    <section
      className={classnames(mini ? styles.miniCard : styles.card, className)}
      style={{ backgroundImage: `url(${backgroundImgUrl})` }}
    >
      {!mini && <img className={styles.foregroundImg} src={foregroundImgUrl} />}
      {!mini && <span className={classnames(styles.explanation, explanationClassName)}>{explanationText}</span>}
      <Button className={styles.btn} type="primary" onClick={onClick}>
        {btnText}
      </Button>
    </section>
  );
};

export default memo(LockedCard);
